import { Tag, Tooltip, Typography } from 'antd';
import { HTMLAttributes, ReactElement, ReactNode, memo, useCallback, useEffect, useId, useMemo, useState } from 'react';
import TemplateCheckbox from 'components/ui/composed/template/TemplateCheckbox';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { cloneDeep, kebabCase } from 'lodash';
import useDeclarations from 'hooks/useDeclarations';
import { FormType, useDeclarationContext } from 'views/declarations/common/DeclarationContext';
import { CardContext, FieldData } from 'components/ui/composed/template/CardContext';
import { useProductTemplateContext } from 'utils/ProductTemplateContext';
import SearchCustomerDrawerButton from '../../../../components/ui/composed/searchCustomer/SearchCustomerDrawerButton';
import { Customer } from '../../../../store/customers/customer';
import { LFormCardContainer, Meta, SCard, TagsContainer } from './styles';

interface FormCardContainerProps extends HTMLAttributes<HTMLDivElement> {
    oneColumn?: boolean;
    normal?: boolean;
    invoice?: boolean;
}

export const FormCardContainer = ({ oneColumn, normal, invoice, ...divProps }: FormCardContainerProps) => {
    const { template, isViewOnly } = useTemplateContext();

    return (
        <LFormCardContainer
            {...divProps}
            viewOnly={isViewOnly}
            template={template}
            oneColumn={oneColumn}
            normal={normal}
            invoice={invoice}
        />
    );
};

interface Props {
    children: ReactNode;
    title: string;
    onSelectCustomer?: (customer: Customer, onClose: () => void) => void;
    disableTemplateCheckbox?: boolean;
    mic?: { path: string; required?: boolean }; // Multiple Items Card
    tags?: ReactNode;
    refNumber?: string | string[];
    special?: {
        partyPath?: string;
    };
    cardButtons?: ReactNode[] | false | null | undefined;
    error?: string;
    hidden?: boolean;
    section?: boolean;
}

const NewFormCard = ({
    children,
    title,
    onSelectCustomer,
    disableTemplateCheckbox,
    mic,
    tags,
    refNumber: refNumberProp,
    special,
    cardButtons,
    error,
    hidden,
    section: isSection,
}: Props): ReactElement | null => {
    const { form, registerCard, unregisterCard } = useDeclarationContext();
    const { form: templateForm, template, templateFormik, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { declarationTemplate: productTemplateDeclarationTemplate, inProductTemplate } = useProductTemplateContext();
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const location = useLocation();
    const inViewOnly = location.pathname.includes('view-only');
    const [fields, setFields] = useState<FieldData[]>([]);
    const { declarationTemplate } = useDeclarations();
    const { declaration } = useDeclarations();
    const { productId } = useParams();

    const inProductForm = Boolean(productId);
    const cardId = useId();

    const templateData = useMemo(
        () => productTemplateDeclarationTemplate?.template || declarationTemplate?.template,
        [productTemplateDeclarationTemplate, declarationTemplate?.template]
    );

    const registerField = (fieldData: FieldData) => {
        setFields((prev) => {
            const index = prev.findIndex((el) => fieldData.path === el.path);
            if (index === -1) prev.push(fieldData);
            else prev[index] = fieldData;
            return [...prev];
        });
    };
    const registerSection = (sectionFields: FieldData[]) => {
        for (const sectionField of sectionFields) {
            registerField(sectionField);
        }
    };

    const cardHidden = useMemo(() => {
        if (template || !fields.length) return false;
        if (!fields.length) return false; // The card must render once in order to register the fields
        if (fields.every((field) => field.hidden)) return true;
        const _form = inProductTemplate ? 'product' : form;
        if (!_form || _form === 'house-consignment') return false;
        const meta = cloneDeep(templateData?.[_form].meta);
        if (!meta) return false;
        if (mic && Object.keys(meta).includes(mic.path))
            return !meta[mic.path]?.isEditable && !meta[mic.path]?.isViewable;
        return fields.every(
            (field) => meta?.[field.path] && !meta[field.path]?.isEditable && !meta[field.path]?.isViewable
        );
    }, [template, inProductTemplate, form, templateData, mic, fields]);

    const cardForm: FormType = useMemo(
        () => (inProductTemplate ? 'product' : form ?? (inProductForm ? 'product' : 'master')),
        [form, inProductForm, inProductTemplate]
    );

    useEffect(() => {
        registerCard?.({
            id: cardId,
            title,
            visible: !(cardHidden || hidden),
            form: cardForm,
            required: fields.map((field) => field.required).includes(true),
            section: isSection,
        });
    }, [cardHidden, cardForm, cardId, fields, hidden, isSection, registerCard, title]);

    /**
     * Drop the registration when the card leaves the screen. Without this the
     * navigation keeps listing cards that have unmounted - a multiple items card
     * whose `hidden` turned true, or the cards of the form we navigated away
     * from - and clicking them leads nowhere.
     */
    useEffect(() => () => unregisterCard?.(cardId), [cardId, unregisterCard]);

    const cardCheckboxChecked = useMemo(() => {
        if (!template || mic || !templateForm) return;
        const meta = cloneDeep(templateFormik?.values[templateForm]?.meta);
        let _fields = fields;
        if (special?.partyPath) _fields = fields.filter((field) => field.path.includes(special.partyPath!));
        return _fields.some(
            (field) =>
                !field.required &&
                (meta?.[field.path]?.isEditable || meta?.[field.path]?.isViewable || !meta?.[field.path])
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, templateFormik?.values, special?.partyPath]);

    const viewOnly = useMemo(
        () => (inViewOnly && (!outletContext?.amendment || true)) || declaration?.archived,
        [declaration?.archived, inViewOnly, outletContext?.amendment]
    );

    const defaultTags = useMemo(() => {
        const tags = [];
        const tagsComponents = {
            mic: (
                <Tooltip overlay="Multiple Items Card">
                    <Tag color="blue">MIC</Tag>
                </Tooltip>
            ),
            required: <Tag color="red">Required</Tag>,
            ref: (refNum?: string) => <Tag color="rgba(56, 55, 82, 0.2)">{refNum ?? ''}</Tag>,
        };

        if (mic) {
            tags.push(tagsComponents.mic);
            if (mic.required) {
                tags.push(tagsComponents.required);
            }
        }

        if (Array.isArray(refNumberProp)) {
            refNumberProp.forEach((refNumFromArray) => tags.push(tagsComponents.ref(refNumFromArray)));
        } else if (typeof refNumberProp === 'string') {
            tags.push(tagsComponents.ref(refNumberProp));
        }

        return tags;
    }, [mic, refNumberProp]);

    const cardCheckboxDisabled = useMemo(() => {
        const micRequired = mic && mic.required;
        const micAllFieldsRequired = !mic && fields.every((field) => field.required);
        return micRequired || disableTemplateCheckbox || micAllFieldsRequired;
    }, [disableTemplateCheckbox, fields, mic]);

    const _cardCheckboxChecked = useMemo(() => {
        if (!templateForm) return false;
        if (mic) {
            let micMeta = cloneDeep(templateFormik?.values[templateForm]?.meta?.[mic.path]);
            return !micMeta || (micMeta?.isEditable && micMeta?.isViewable);
        }
        return cardCheckboxChecked;
    }, [cardCheckboxChecked, mic, templateForm, templateFormik]);

    const handleCardCheckboxCheck = useCallback(
        (e: CheckboxChangeEvent) => {
            if (!templateForm) return;
            if (mic) {
                templateFormik?.setFieldValue(`${templateForm}.meta.['${mic.path}']`, {
                    isEditable: e.target.checked,
                    isViewable: e.target.checked,
                });
                return;
            }
            let meta = cloneDeep(templateFormik?.values[templateForm]?.meta);
            if (!meta) return;
            fields.forEach((field) => {
                if (special?.partyPath && !field.path.includes(special.partyPath)) return;
                if (!field.required) {
                    meta![field.path] = { isEditable: e.target.checked, isViewable: e.target.checked };
                } else {
                    meta![field.path] = {
                        isEditable: meta![field.path]?.isEditable ?? true,
                        isViewable: true,
                    };
                }
            });
            templateFormik?.setFieldValue(`${templateForm}.meta`, { ...meta });
        },
        [fields, mic, special?.partyPath, templateForm, templateFormik]
    );

    if (cardHidden || hidden) return null;

    return (
        <SCard id={`${kebabCase(title)}-form-card`} data-locator={mic ? mic.path : undefined} error={Boolean(error)}>
            <div style={{ display: 'flex', marginBottom: '1rem', alignItems: 'center' }}>
                <Meta title={title} />
                <TagsContainer>
                    {defaultTags}
                    {tags}
                </TagsContainer>
                <div style={{ display: 'flex', gap: '2rem', marginLeft: 'auto' }}>
                    {!(viewOnly || isTemplateViewOnly) && onSelectCustomer && (
                        <SearchCustomerDrawerButton onSelectCustomer={onSelectCustomer} />
                    )}
                    {cardButtons}
                    {template && !isTemplateViewOnly && (
                        <TemplateCheckbox
                            disabled={cardCheckboxDisabled}
                            size="medium"
                            onChange={handleCardCheckboxCheck}
                            checked={_cardCheckboxChecked}
                        />
                    )}
                </div>
            </div>
            <CardContext.Provider value={{ registerField, registerSection }}>
                {/*
                    Wrap each card with a form to scope the browser
                    autofill only to the card and not for the whole page.
                */}
                <form
                    onSubmit={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                        }
                    }}
                >
                    {children}
                </form>
            </CardContext.Provider>
            {error && <Typography.Text type="danger">{error}</Typography.Text>}
        </SCard>
    );
};

export default memo(NewFormCard);
